import type { ApiClient } from '../../../shared/api/api-client';
import type { OperationalPaymentRow, OperationalPaymentsListParams, OperationalPaymentsListResponse } from './payments.types';
import type { PaymentStatsDto } from '../../superadmin/api/payments-stats.api';

function buildPaymentsQuery(params: OperationalPaymentsListParams): string {
  const sp = new URLSearchParams();
  const limit = params.limit != null && params.limit > 0 ? params.limit : 20;
  sp.set('limit', String(limit));
  if (params.before_id != null && params.before_id > 0) {
    sp.set('before_id', String(params.before_id));
  } else if (params.offset != null && params.offset >= 0) {
    sp.set('offset', String(params.offset));
  }
  if (params.organization_id != null && params.organization_id > 0) {
    sp.set('organization_id', String(params.organization_id));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

function normalizePaymentRow(raw: Record<string, unknown>): OperationalPaymentRow | null {
  const id = typeof raw.id === 'number' ? raw.id : Number(raw.id);
  const organization_id =
    typeof raw.organization_id === 'number' ? raw.organization_id : Number(raw.organization_id);
  const amount_cents =
    typeof raw.amount_cents === 'number' ? raw.amount_cents : Number(raw.amount_cents);
  const currency = typeof raw.currency === 'string' ? raw.currency : '';
  const status = typeof raw.status === 'string' ? raw.status : '';
  const provider = typeof raw.provider === 'string' ? raw.provider : '';
  const created_at = typeof raw.created_at === 'string' ? raw.created_at : '';
  const updated_at = typeof raw.updated_at === 'string' ? raw.updated_at : '';
  if (!Number.isFinite(id) || id < 1) return null;
  if (!Number.isFinite(organization_id) || organization_id < 1) return null;
  if (!Number.isFinite(amount_cents)) return null;
  if (!created_at || !updated_at) return null;
  const idempotency_key =
    raw.idempotency_key === null || raw.idempotency_key === undefined
      ? null
      : typeof raw.idempotency_key === 'string'
        ? raw.idempotency_key
        : null;
  const reference_type =
    raw.reference_type === null || raw.reference_type === undefined
      ? null
      : typeof raw.reference_type === 'string'
        ? raw.reference_type
        : null;
  const reference_id =
    raw.reference_id === null || raw.reference_id === undefined
      ? null
      : typeof raw.reference_id === 'number'
        ? raw.reference_id
        : Number(raw.reference_id);
  const provider_ref =
    raw.provider_ref === null || raw.provider_ref === undefined
      ? null
      : typeof raw.provider_ref === 'string'
        ? raw.provider_ref
        : null;
  const metadata =
    raw.metadata && typeof raw.metadata === 'object' && !Array.isArray(raw.metadata)
      ? (raw.metadata as Record<string, unknown>)
      : undefined;
  return {
    id,
    organization_id,
    idempotency_key,
    amount_cents,
    currency,
    status,
    provider,
    reference_type,
    reference_id: reference_id != null && Number.isFinite(reference_id) ? reference_id : null,
    provider_ref,
    metadata,
    created_at,
    updated_at
  };
}

function normalizeListResponse(raw: unknown): OperationalPaymentsListResponse {
  const obj = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const itemsRaw = Array.isArray(obj.items) ? obj.items : [];
  const items: OperationalPaymentRow[] = [];
  for (const row of itemsRaw) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue;
    const n = normalizePaymentRow(row as Record<string, unknown>);
    if (n) items.push(n);
  }
  const total = typeof obj.total === 'number' && Number.isFinite(obj.total) ? obj.total : items.length;
  return { items, total };
}

export async function fetchOperationalPaymentsList(
  client: ApiClient,
  params: OperationalPaymentsListParams = {}
): Promise<OperationalPaymentsListResponse> {
  const q = buildPaymentsQuery(params);
  const raw = await client.request<unknown>(`/payments${q}`, { method: 'GET' });
  return normalizeListResponse(raw);
}

/** Одна запись (`GET /api/v1/payments/{id}`), тот же контракт полей, что в `items[]` списка. */
export async function fetchOperationalPaymentById(client: ApiClient, id: number): Promise<OperationalPaymentRow> {
  const raw = await client.request<unknown>(`/payments/${encodeURIComponent(String(id))}`, { method: 'GET' });
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Некорректный ответ сервера при загрузке платежа');
  }
  const row = normalizePaymentRow(raw as Record<string, unknown>);
  if (!row) {
    throw new Error('Некорректный ответ сервера при загрузке платежа');
  }
  return row;
}

/** Статистика платежей для своей организации (GET /api/v1/payments/stats). */
export async function fetchOrgPaymentsStats(
  client: ApiClient,
  from?: string,
  to?: string
): Promise<PaymentStatsDto> {
  const sp = new URLSearchParams();
  if (from) sp.set('from', from);
  if (to) sp.set('to', to);
  const qs = sp.toString();
  const raw = await client.request<unknown>(`/payments/stats${qs ? '?' + qs : ''}`, { method: 'GET' });
  return raw as PaymentStatsDto;
}
